<template lang="pug">
p.text-red-500(v-if="search === '' && loaded && result.length === 0") {{ $t("noData") }}
el-select(
  v-else,
  v-model="model",
  v-cancel-read-only,
  filterable,
  remote,
  :remote-method="filter",
  :multiple="props.multiple",
  :placeholder="props.placeholder",
  placement="bottom",
  suffix-icon="ArrowDown",
  :remote-show-suffix="true",
  value-key="uuid",
  :disabled="props.disabled"
)
  template(#prefix)
    font-awesome-icon(:icon="['fas', 'fa-magnifying-glass']")
  el-option-group(v-lazy-load="loadMoreData")
    el-option(
      v-for="item in result",
      :key="item.uuid",
      :value="type === 'sop' ? item : item.uuid",
      :label="item.name"
    )
  template(v-if="loading", #footer)
    .option-loading(v-loading="true")
</template>

<script setup lang="ts">
import type { PaginationQuery } from "@shared/services/common";
import { computed, onMounted, onUnmounted, ref, unref, watch } from "vue";
import type { Pagination } from "@shared/services/common";
import vLazyLoad from "@shared/directives/elSelectInfiniteScroll";
import { throttle, debounce } from "lodash";
import { cancelReadOnly as vCancelReadOnly } from "@shared/directives/select-readonly";
import { type SOP, type SOPQuery, getSops, getTicketSops } from "../service";

const result = ref<SOP[]>([]);

const selectedSops = ref<SOP[]>([]);

const page = ref<Pagination>(null);
// NOTE: all data loading is happening in this component itself, since we need to support infinite scrolling and search across an arbitrary number of input field that are being rendered to the user
onMounted(() => {
  initData();
  loadMoreData(true);
});

const props = defineProps<{
  query: SOPQuery;
  modelValue?: Array<SOP | string> | SOP | string | null;
  // NOTE: type is either determined by the modelValue, or it can be set manually.
  type?: "id" | "sop";
  multiple?: boolean;
  placeholder?: string;
  disabled?: boolean;
}>();

watch(() => props.modelValue, initData);

const type = computed(() => {
  let modelType = null;
  if (props.modelValue) {
    if (props.multiple) {
      modelType = typeof props.modelValue[0] === "string" ? "string" : "sop";
    } else {
      modelType = typeof props.modelValue === "string" ? "string" : "sop";
    }
  }
  if (props.type) {
    // If model is not as specified, throw error
    if (modelType !== null && modelType !== props.type) {
      throw Error("Misconfigured: type not matching model");
    }
    return props.type;
  } else if (modelType !== null) {
    return modelType;
  }
  // Fallback is sop
  return "sop";
});

function initData() {
  if (props.modelValue) {
    // If data is set as sops, we can use them directly
    if (type.value === "sop") {
      selectedSops.value = props.multiple
        ? (props.modelValue as SOP[])
        : ([props.modelValue] as SOP[]);
      setResult([...result.value, ...selectedSops.value]);
    } else {
      loadSelectedSops();
    }
  }
}

const emit = defineEmits(["update:modelValue"]);

const pagination = ref<PaginationQuery>({
  page: 0,
  pageSize: 10,
});

const request = ref<SOPQuery>({});

const loading = ref(false);
const loaded = ref(false);
const error = ref(false);

const loadSearch = ref();

let abortController: AbortController | null = null;

async function loadMoreData(reload?: boolean) {
  // NOTE: if we already load the current search query, do not reload
  if (
    !reload &&
    request.value.search === loadSearch.value &&
    (loading.value || (loaded.value && !hasMorePages.value))
  ) {
    return;
  }
  // NOTE: search queries might take time depending on complexity, we have to make sure that we actually only retrieve the last request, thus prior requests have to be cancelled
  if (abortController !== null) {
    try {
      abortController.abort();
    } catch (e) {
      // noop
    }
    abortController = null;
  }
  const query = { ...props.query, ...request.value };
  loadSearch.value = request.value.search;
  loaded.value = false;
  loading.value = true;
  pagination.value.page++;
  abortController = new AbortController();
  const response = await getSops(
    { ...pagination.value, ...query },
    { signal: abortController.signal },
  ).catch((e) => {
    if (e && e?.code === "ERR_CANCELED") {
      // We cancelled the request in favor of a new search query
      e.dontLog = true;
      throw e;
    }
    error.value = true;
    loading.value = false;
    loaded.value = true;
    throw e;
  });
  // NOTE: if we load the first page of a search, we want to override all previous data.
  // This is important because a request might finish between being cancelled and a data reset by a new request, in which case its data will be in the result.
  // In case of a next page load, we have to merge the data
  if (pagination.value.page === 1) {
    setResult(response.data.result);
  } else {
    setResult([...result.value, ...response.data.result]);
  }
  page.value = {
    total: response.data.total,
    pagesTotal: response.data.pagesTotal,
  };
  loading.value = false;
  loaded.value = true;
}

function setResult(sops: SOP[]) {
  // Filter for uniqueness, note that this isn't very effective but okay for human possible scroll sizes, this makes sense since there might be items created while scrolling, resulting in differences in paging
  const ids = new Set();
  result.value = [...sops, ...selectedSops.value].filter((item) => {
    if (!ids.has(item.uuid)) {
      ids.add(item.uuid);
      return true;
    }
    return false;
  });
}

async function loadSelectedSops() {
  // Load the data of selected sops to show proper names, if set as trings, otherwise the data does not have to be loaded
  if (type.value === "string") {
    const uuids = props.modelValue as string[];
    const response = await getSops({ page: 1, pageSize: 50, uuid: uuids });
    selectedSops.value = response.data.result;
    setResult(result.value);
  }
}

watch(
  () => props.query,
  (oldVal, newVal) => {
    // NOTE: this isn't pretty, but it avoids unwanted reloaded if the query object hasn't changed at all
    if (JSON.stringify(oldVal) === JSON.stringify(newVal)) {
      return;
    }
    pagination.value.page = 0;
    setResult([]);
    page.value.pagesTotal = null;
    loading.value = true;
    loadMoreData(true);
  },
);

const debouncedLoadData = debounce(loadMoreData, 300);

const search = ref<string>("");

watch(search, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    if (newValue !== "") {
      // reset the current data
      request.value.search = newValue;
      pagination.value.page = 0;
      setResult([]);
      if (page.value) {
        page.value.pagesTotal = null;
      }
      loading.value = true;
      debouncedLoadData();
    } else {
      request.value.search = "";
      pagination.value.page = 0;
      setResult([]);
      if (page.value) {
        page.value.pagesTotal = null;
      }
      loading.value = true;
      loadMoreData();
    }
  }
});

function filter(input: string) {
  search.value = input;
}

const hasMorePages = computed(() => {
  if (page.value.pagesTotal === null) {
    // inital load
    return true;
  }
  if (page.value !== null) {
    return page.value.pagesTotal > pagination.value.page;
  }
  return false;
});

const model = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    if (value === "") {
      return;
    }
    emit("update:modelValue", value);
  },
});
</script>

<style scoped>
.option-loading {
  height: 50px;
}
</style>
